import { useState, useLayoutEffect, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Dropdown from '../../common/Dropdown'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ToggleSwitch from '../../common/ToggleSwitch'
import { toast } from '../../common/Toast'
import SimpleTextInput from '../../common/SimpleTextInput'
import { getFileIcon } from '../../../utils/functions'
import { firmwareUploadWritePermission } from '../../../utils/permissionValidation'
import { updateFirmwareFile } from '../../../actions/firmware'
import { getRevisionNumberValidation } from '../../../utils/requests/firmwareAPI'
import '../uploads.scss'

const EditSoftwareFileModal = (props) => {
  const { t } = useTranslation()
  const { selectedFile, closeModal, showSpinner, setShowSpinner } = props
  const [fileTitle, setFileTitle] = useState('')
  const [softwareLabel, setSoftwareLabel] = useState('')
  const [model, setModel] = useState(null)
  const [firmwareType, setFirmwareType] = useState(null)
  const [prefix, setPrefix] = useState(null)
  const [revisionNumber, setRevisionNumber] = useState('')
  const [modelOptions, setModelOptions] = useState([])
  const [status, setStatus] = useState(false)
  const [haveError, setHaveError] = useState({})
  const statuses = ['inactive', 'active']

  useLayoutEffect(() => {
    if (selectedFile) {
      setStatus(selectedFile.isActive)
      setFileTitle(selectedFile.fileTitle || '')
      setSoftwareLabel(selectedFile.softwareLabel || '')
      setModel(selectedFile.models[0] || null)
      setFirmwareType(selectedFile.firmwareType)
      setPrefix(selectedFile.prefix)
      setRevisionNumber(selectedFile.prefixNumber || '')

      if (selectedFile.models && selectedFile.models.length && props.prefixes.length) {
        const matchingPrefix = props.prefixes.find((prefix) => prefix.prefix === selectedFile.prefix)
        setModelOptions([
          ...new Set(
            props.prefixes
              .filter((prefix) => prefix.productType === matchingPrefix?.productType)
              .map((prefix) => prefix.models)
              .flat(),
          ),
        ])
      }
    }
  }, [selectedFile])

  useEffect(() => {
    if (model && model !== selectedFile.models[0]) {
      setFirmwareType(null)
      setPrefix(null)
      setRevisionNumber('')
      setHaveError({})
    }
  }, [model])

  useEffect(() => {
    if (firmwareType && firmwareType !== selectedFile.firmwareType) {
      setPrefix(null)
      setRevisionNumber('')
      setHaveError({})
    }
  }, [firmwareType])

  useEffect(() => {
    if (prefix && prefix !== selectedFile.prefix) {
      setRevisionNumber('')
      setHaveError({})
    }
  }, [prefix])

  const fileHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <div className="label">{t('status')}</div>
        <ToggleSwitch checked={status} onChange={() => setStatus((prevValue) => !prevValue)} isDisabled={showSpinner} />
        <div className="status">{t(statuses[Number(status)])}</div>
      </div>
    )
  }

  const fileFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={() => closeModal()}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton
          className="submit-button"
          onClick={handleEditFile}
          disabled={showSpinner || hasSameInput()}
          requiredPermission={firmwareUploadWritePermission}
        >
          {t('save')}
        </SimpleButton>
      </>
    )
  }

  const hasSameInput = () => {
    return (
      status === selectedFile.isActive &&
      fileTitle === selectedFile.fileTitle &&
      softwareLabel === selectedFile.softwareLabel &&
      model === (selectedFile.models[0] || null) &&
      firmwareType === selectedFile.firmwareType &&
      prefix === selectedFile.prefix &&
      revisionNumber === (selectedFile.prefixNumber || '')
    )
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!fileTitle.trim()) {
      errors = { ...errors, fileTitle: true }
      isValid = false
    }
    
    if (!softwareLabel.trim()) {
      errors = { ...errors, softwareLabel: true }
      isValid = false
    }

    if (!model) {
      errors = { ...errors, model: true }
      isValid = false
    }

    if (!firmwareType) {
      errors = { ...errors, firmwareType: true }
      isValid = false
    }

    if (!prefix) {
      errors = { ...errors, prefix: true }
      isValid = false
    }

    if (!revisionNumber.trim() || haveError[`revisionNumber`]) {
      errors = { ...errors, revisionNumber: true }
      isValid = false
    }

    setHaveError(errors)
    return isValid
  }

  const handleEditFile = () => {
    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    const bodyParams = {
      isActive: status,
      fileTitle: fileTitle.trim(),
      softwareLabel: softwareLabel.trim(),
      models: [model],
      firmwareType: firmwareType,
      firmwareTypeId: props.types.find((t) => t.type === firmwareType)?.id,
      prefix: prefix,
      prefixNumber: revisionNumber.trim(),
    }

    props.actions.updateFirmwareFile(selectedFile.id, bodyParams).then(() => {
      closeModal()
    })
  }

  const handleCheckValidPrefixNo = async () => {
    if (prefix && revisionNumber.trim().length > 0 && revisionNumber.trim() !== selectedFile.prefixNumber) {
      setShowSpinner(true)

      try {
        const revisionValidationResponse = await getRevisionNumberValidation(prefix, revisionNumber.trim())

        if (revisionValidationResponse.data) {
          setHaveError((prevError) => ({
            ...prevError,
            [`revisionNumber`]: true,
          }))

          return
        }
      } catch (error) {
        toast.error(t('error.something_wrong'))

        setHaveError((prevError) => ({
          ...prevError,
          [`revisionNumber`]: true,
        }))

        return
      } finally {
        setShowSpinner(false)
      }
    }

    setHaveError((prevError) => ({
      ...prevError,
      [`revisionNumber`]: false,
    }))
  }

  const getFirmwareTypeOptionsForModel = (model) => {
    if (!model) return []
    const allowedTypes = [
      ...new Set(props.prefixes.filter((p) => p.models?.includes(model) && p.firmwareType && p.firmwareType !== 'Instruction').map((p) => p.firmwareType)),
    ]
    return allowedTypes.map((type) => ({ key: type, value: type }))
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="uploads-modal"
        title={t('edit_file')}
        headerContent={fileHeaderContent()}
        footerContent={fileFooterContent()}
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('file_name')}</p>
              <div className="value">
                {getFileIcon(selectedFile.fileName)}
                {selectedFile.fileName}
              </div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('total_downloads')}</p>
              <div className="value">{selectedFile.totalDownloads || 0}</div>
            </div>
          </div>
          <div className="separator" />
          <div className="section-title">{t('settings').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('firmware_context.file_display_title')}*`}</p>
              <SimpleTextInput
                value={fileTitle}
                onChange={(value) => setFileTitle(value)}
                onClear={() => setFileTitle('')}
                hasError={haveError[`fileTitle`]}
                disabled={!prefix || !revisionNumber}
              />
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('firmware_context.device_software_label')}</p>
              <SimpleTextInput
                value={softwareLabel}
                onChange={(value) => setSoftwareLabel(value)}
                onClear={() => setSoftwareLabel('')}
                hasError={haveError[`softwareLabel`]}
                disabled={!prefix || !revisionNumber}
              />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('model')}*`}</p>
              <Dropdown
                placeholder={t('please_select')}
                options={modelOptions.map((m) => ({ key: m, value: m }))}
                initialSelectedKey={model}
                onChange={(model) => setModel(model.key)}
                hasError={haveError[`model`]}
              />
            </div>
            <div className="input-wrapper">
              <p className="input-option">{`${t('firmware_context.firmware_type')}*`}</p>
              <Dropdown
                placeholder={t('please_select')}
                options={getFirmwareTypeOptionsForModel(model)}
                initialSelectedKey={firmwareType}
                onChange={(type) => setFirmwareType(type.key)}
                hasError={haveError[`firmwareType`]}
                isDisabled={!model}
              />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('prefix')}*`}</p>
              <Dropdown
                placeholder={t('please_select')}
                options={props.prefixes
                  .filter((p) => p.models.includes(model) && p.firmwareType === firmwareType)
                  .map((t) => ({ key: t.prefix, value: t.prefix }))}
                initialSelectedKey={prefix}
                onChange={(p) => setPrefix(p.key)}
                hasError={haveError[`prefix`]}
                isDisabled={!model || !firmwareType}
              />
            </div>
            <div className="input-wrapper">
              <p className="input-option">{`${t('document_context.revision_number')}*`}</p>
              <input
                type="text"
                className={'name-input' + (haveError[`revisionNumber`] ? ' has-error' : '')}
                value={revisionNumber}
                onChange={(e) => {
                  setRevisionNumber(e.target.value)
                  setFileTitle(`${prefix}R${e.target.value}`)
                  setSoftwareLabel(`${prefix}R${e.target.value}`)
                }}
                onBlur={() => handleCheckValidPrefixNo()}
                disabled={!prefix}
              />
              {haveError[`revisionNumber`] && revisionNumber.length > 0 && <span className="error-message">{t('error.revision_number_already_used')}</span>}
            </div>
          </div>
        </div>
      </PageModal>
    </>
  )
}

function stateToProps({ firmware }) {
  return {
    prefixes: firmware?.prefixes || [],
    types: firmware?.types || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        updateFirmwareFile,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(EditSoftwareFileModal)

